const prefix = 'editorial-mobile-menu__';
const normalize = value => value.toLocaleLowerCase().replace(/ae/g, 'a').replace(/oe/g, 'o').replace(/ue/g, 'u').normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/ß/g, 'ss');
const searchWords = value => value.match(/[\p{L}\p{N}]+/gu) || [];
const searchField = value => {
  const text = normalize(value);
  return { text, words: searchWords(text) };
};
// Bounded edit distance, including adjacent swapped letters ("patsa").
// Free word edges preserve partial matches and compounds such as "Vollkornpasta".
const wordDistance = (query, word, limit) => {
  if (word.length < query.length - limit) return limit + 1;
  const target = word;
  let previous = Array(target.length + 1).fill(0);
  let beforePrevious;
  for (let i = 1; i <= query.length; i += 1) {
    const current = [i];
    for (let j = 1; j <= target.length; j += 1) {
      current[j] = Math.min(previous[j] + 1, current[j - 1] + 1, previous[j - 1] + (query[i - 1] === target[j - 1] ? 0 : 1));
      if (i > 1 && j > 1 && query[i - 1] === target[j - 2] && query[i - 2] === target[j - 1]) {
        current[j] = Math.min(current[j], beforePrevious[j - 2] + 1);
      }
    }
    if (Math.min(...current) > limit) return limit + 1;
    beforePrevious = previous;
    previous = current;
  }
  return Math.min(...previous.slice(1));
};
const matchWord = (query, field) => {
  if (field.text.includes(query)) return 3;
  // Short words stay precise; very long pasted tokens avoid quadratic work.
  if (query.length < 4 || query.length > 64) return 0;
  const limit = query.length >= 8 ? 2 : 1;
  let distance = limit + 1;
  for (const word of field.words) distance = Math.min(distance, wordDistance(query, word, limit));
  return distance <= limit ? 3 - distance : 0;
};
const element = (tag, className, text) => {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
};
const icon = kind => {
  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  svg.setAttribute('viewBox', '0 0 24 24');
  svg.setAttribute('aria-hidden', 'true');
  const path = document.createElementNS(svg.namespaceURI, 'path');
  path.setAttribute('d', kind === 'next' ? 'm9 5 7 7-7 7' : 'M4 12h15m-6-6 6 6-6 6');
  svg.append(path);
  return svg;
};

class EditorialMobileMenu extends HTMLElement {
  connectedCallback() {
    if (this.controller) return;
    this.controller = new AbortController();
    const options = { signal: this.controller.signal };
    this.panel = this.querySelector('[data-mm-panel]');
    this.scroller = this.querySelector('[data-mm-scroll]');
    this.heading = this.querySelector('[data-mm-title]');
    this.input = this.querySelector('[data-mm-input]');
    this.backButton = this.querySelector('[data-mm-back]');
    this.breadcrumbs = this.querySelector('[data-mm-breadcrumbs]');
    this.owner = this.closest('[data-editorial-header]').id;
    this.historyEnabled = !window.Shopify?.designMode;
    this.nodes = new Map();
    this.positions = new Map();
    this.current = null;
    this.searching = false;
    this.query = '';
    this.searchOrigin = null;
    this.searchScroll = 0;
    this.depth = 0;
    this.isOpen = false;
    this.pendingClose = false;
    this.roots = this.readTree(this.querySelector('[data-mm-source] > ul'));
    this.index(this.roots);
    const saved = this.ownHistoryState();
    if (saved) {
      this.restore(saved);
      this.wantsOpen = true;
    }
    this.render('', false);

    this.input.addEventListener('focus', () => this.startSearch(), options);
    this.input.addEventListener('input', () => {
      this.query = this.input.value;
      this.searchScroll = 0;
      this.render('', false);
      this.writeHistory(true);
    }, options);
    this.querySelector('[data-mm-search]').addEventListener('submit', event => {
      event.preventDefault();
      this.input.blur();
      this.focusHeading();
    }, options);
    this.querySelector('[data-mm-clear]').addEventListener('click', () => {
      this.query = '';
      this.searchScroll = 0;
      this.render('', false);
      this.writeHistory(true);
      this.input.focus();
    }, options);
    this.backButton.addEventListener('click', () => this.back(), options);
    this.addEventListener('click', event => {
      const control = event.target.closest('[data-mm-go]');
      if (control && this.contains(control)) this.navigate(control.dataset.mmGo);
    }, options);
    this.addEventListener('keydown', event => {
      if (event.key === 'Escape' && this.searching) {
        event.preventDefault();
        event.stopPropagation();
        this.back();
      }
    }, options);
    window.addEventListener('popstate', () => {
      if (this.pendingClose) { this.pendingClose = false; this.depth = 0; return; }
      const state = this.ownHistoryState();
      if (state) {
        this.stash(false);
        this.restore(state);
        if (!this.isOpen) this.dispatchEvent(new CustomEvent('mobile-menu:open', { bubbles: true }));
        this.render('back');
      } else if (this.isOpen) {
        this.depth = 0;
        this.dispatchEvent(new CustomEvent('mobile-menu:close', { bubbles: true }));
      }
    }, options);
  }

  disconnectedCallback() {
    this.controller?.abort();
    this.controller = null;
    cancelAnimationFrame(this.focusFrame);
    clearTimeout(this.statusTimer);
  }

  readTree(list) {
    return [...(list?.children || [])].map(item => {
      const link = item.querySelector(':scope > [data-mm-link]');
      const node = {
        id: item.dataset.mmNode, name: link.textContent.trim(), link,
        hint: item.querySelector(':scope > [data-mm-hint]')?.textContent.trim() || '',
        illustration: item.querySelector(':scope > [data-mm-icon]')?.content.querySelector('svg'),
        productImage: item.querySelector(':scope > [data-mm-product-image]')?.content.querySelector('img'),
        children: this.readTree(item.querySelector(':scope > ul')),
        navigable: Boolean(link.getAttribute('href') && link.getAttribute('href') !== '#'),
      };
      // Existing "View all" entries are represented once by the overview link.
      node.children = node.children.filter(child => !(node.navigable && child.link.href === link.href && !child.children.length));
      // Only collapse a duplicate when doing so cannot lose a distinct destination.
      while (node.children.length === 1) {
        const child = node.children[0];
        if (!child.children.length || normalize(child.name) !== normalize(node.name) || (node.navigable && child.link.href !== link.href)) break;
        if (!node.navigable) { node.link = child.link; node.navigable = child.navigable; }
        node.children = child.children;
      }
      return node;
    });
  }

  index(nodes, ancestors = []) {
    nodes.forEach(node => {
      node.ancestors = ancestors;
      node.searchOwn = searchField(node.name + ' ' + node.hint);
      node.searchContext = searchField(ancestors.map(parent => parent.name).join(' '));
      this.nodes.set(node.id, node);
      this.index(node.children, [...ancestors, node]);
    });
  }

  ownHistoryState() {
    const state = history.state?.editorialMobileMenu;
    return this.historyEnabled && state?.owner === this.owner ? state : null;
  }

  writeHistory(replace = false) {
    if (!this.historyEnabled || !this.isOpen) return;
    if (!replace) this.depth += 1;
    history[replace ? 'replaceState' : 'pushState']({
      ...history.state,
      editorialMobileMenu: {
        owner: this.owner, depth: this.depth, current: this.current?.id || null,
        searching: this.searching, query: this.query, origin: this.searchOrigin?.id || null,
        positions: [...this.positions], searchScroll: this.searchScroll,
      },
    }, '');
  }

  restore(state) {
    this.current = this.nodes.get(state.current) || null;
    this.searching = Boolean(state.searching);
    this.query = state.query || '';
    this.searchOrigin = this.nodes.get(state.origin) || null;
    this.depth = state.depth || 1;
    this.positions = new Map(state.positions || []);
    this.searchScroll = state.searchScroll || 0;
  }

  open() {
    if (this.pendingClose) return false;
    this.isOpen = true;
    if (!this.ownHistoryState()) { this.depth = 0; this.writeHistory(); }
    this.wantsOpen = false;
    this.render();
    return true;
  }

  close() {
    if (!this.isOpen) return;
    this.stash();
    this.isOpen = false;
    cancelAnimationFrame(this.focusFrame);
    if (this.ownHistoryState() && this.depth > 0) {
      this.pendingClose = true;
      history.go(-this.depth);
    }
  }

  stash(updateHistory = true) {
    if (this.searching) this.searchScroll = this.scroller.scrollTop;
    else this.positions.set(this.current?.id || 'root', this.scroller.scrollTop);
    if (updateHistory && this.ownHistoryState()) this.writeHistory(true);
  }

  navigate(id) {
    this.stash();
    const previous = this.current;
    this.current = this.nodes.get(id) || null;
    this.searching = false;
    this.query = '';
    this.writeHistory();
    const backwards = previous && (!this.current || previous.ancestors.includes(this.current));
    this.render(backwards ? 'back' : 'forward');
    if (backwards) {
      const trigger = [...this.panel.querySelectorAll('[data-mm-go]')].find(button => button.dataset.mmGo === previous.id);
      if (trigger) { cancelAnimationFrame(this.focusFrame); trigger.focus({ preventScroll: true }); }
    }
  }

  startSearch() {
    if (this.searching) return;
    this.stash();
    this.searchOrigin = this.current;
    this.searching = true;
    this.searchScroll = 0;
    this.writeHistory();
    this.render('', false);
  }

  back() {
    if (this.searching) this.navigate(this.searchOrigin?.id || 'root');
    else this.navigate(this.current?.ancestors.at(-1)?.id || 'root');
  }

  goButton(name, id, className) {
    const button = element('button', className, name);
    button.type = 'button';
    button.dataset.mmGo = id;
    return button;
  }

  row(node, search = false) {
    const category = node.children.length > 0;
    const row = category ? this.goButton('', node.id) : node.link.cloneNode(false);
    row.removeAttribute('data-mm-link');
    row.className = `${prefix}row type-nav type-nav--large${category ? '' : ` ${prefix}row--leaf`}`;
    if (category) row.setAttribute('aria-controls', this.panel.id);
    if (search && !category && node.productImage) {
      const media = element('span', `${prefix}product-image`);
      media.setAttribute('aria-hidden', 'true');
      media.append(node.productImage.cloneNode(true));
      row.append(media);
    }
    if (!search && !this.current && node.illustration) {
      const illustration = element('span', `${prefix}illustration`);
      illustration.setAttribute('aria-hidden', 'true');
      illustration.append(node.illustration.cloneNode(true));
      row.append(illustration);
    }
    const copy = element('span', '', node.name);
    const context = node.ancestors.map(parent => parent.name).join(' / ');
    const hint = search ? (category ? `${this.dataset.categoryLabel}${context ? ' · ' + context : ''}` : context) : node.hint;
    if (hint) copy.append(element('span', `${prefix}hint type-caption`, hint));
    row.append(copy, icon(category ? 'next' : 'arrow'));
    return row;
  }

  list(nodes, search = false) {
    const nav = element('nav');
    nav.setAttribute('aria-labelledby', this.heading.id);
    const list = element('ul', `${prefix}list${!this.current && !search ? ` ${prefix}list--root` : ''}`);
    nodes.forEach(node => { const li = element('li'); li.append(this.row(node, search)); list.append(li); });
    nav.append(list);
    return nav;
  }

  render(direction = '', focus = true) {
    this.panel.id ||= this.heading.id + '-panel';
    this.panel.replaceChildren();
    this.panel.removeAttribute('data-direction');
    if (direction) {
      // Resolve the cleared CSS animation before reusing the same direction.
      // Otherwise consecutive forward/back steps keep the finished animation.
      this.panel.getAnimations();
      this.panel.dataset.direction = direction;
    }
    this.input.value = this.query;
    this.querySelector('[data-mm-clear]').hidden = !this.query;
    this.querySelector('[data-mm-menu-label]').hidden = Boolean(this.current || this.searching);
    this.backButton.hidden = !this.current && !this.searching;
    this.backButton.setAttribute('aria-label', this.searching ? this.dataset.searchBackLabel : this.dataset.backLabel.replace('[title]', this.current?.ancestors.at(-1)?.name || this.dataset.categoriesLabel));
    this.breadcrumbs.replaceChildren();
    this.breadcrumbs.hidden = !this.current || this.searching;
    if (!this.breadcrumbs.hidden) {
      this.breadcrumbs.append(this.goButton(this.dataset.categoriesLabel, 'root', 'button-text-link'));
      this.current.ancestors.forEach(node => this.breadcrumbs.append(icon('next'), this.goButton(node.name, node.id, 'button-text-link')));
    }
    this.querySelector('[data-mm-bottom]').hidden = this.searching;
    clearTimeout(this.statusTimer);
    this.querySelector('[data-mm-status]').textContent = '';
    const titleIllustration = this.dataset.showTitleIcon !== 'false' && !this.searching ? this.current?.illustration : null;
    this.heading.classList.toggle(`${prefix}title--illustrated`, Boolean(titleIllustration));
    if (this.searching) this.renderSearch();
    else {
      this.heading.textContent = this.current?.name || this.dataset.rootTitle;
      if (titleIllustration) {
        const illustration = element('span', `${prefix}illustration`);
        illustration.setAttribute('aria-hidden', 'true');
        illustration.append(titleIllustration.cloneNode(true));
        this.heading.replaceChildren(illustration, element('span', `${prefix}title-text`, this.current.name));
      }
      this.panel.append(this.list(this.current?.children || this.roots));
      if (this.current?.navigable) {
        const overview = this.current.link.cloneNode(false);
        overview.removeAttribute('data-mm-link');
        overview.className = `${prefix}overview type-nav`;
        overview.append(element('span', '', this.dataset.allLabel), icon('arrow'));
        if (this.dataset.allItemsBelow === 'true') {
          overview.classList.add(`${prefix}overview--below`);
          this.panel.append(overview);
        } else this.panel.prepend(overview);
      }
      if (!this.current) this.panel.append(this.querySelector('[data-mm-extras]').content.cloneNode(true));
      this.scroller.scrollTop = this.positions.get(this.current?.id || 'root') || 0;
    }
    if (focus) this.focusHeading();
  }

  renderSearch() {
    const words = [...new Set(searchWords(normalize(this.query)))];
    this.heading.textContent = words.length ? this.dataset.resultsTitle : this.dataset.searchTitle;
    if (!words.length) {
      this.panel.append(this.querySelector('[data-mm-search-help]').content.cloneNode(true));
      const shortcuts = element('div', `${prefix}suggestions`);
      this.roots.slice(0, 4).forEach(node => shortcuts.append(this.goButton(node.name, node.id, 'button button--utility type-button')));
      this.panel.append(shortcuts);
    } else {
      const matches = [...this.nodes.values()].map(node => {
        const own = words.map(word => matchWord(word, node.searchOwn));
        const context = words.map(word => matchWord(word, node.searchContext));
        return {
          node,
          matches: own.some(Boolean) && own.every((score, i) => score || context[i]),
          exact: own.some(score => score === 3) && own.every((score, i) => score === 3 || context[i] === 3),
          score: own.reduce((total, score) => total + score, 0) + (node.children.length ? 1 : 0),
        };
      }).filter(item => item.matches);
      // Only broaden an empty search; "Pasta" should not add "Pistazien".
      const exactMatches = matches.filter(item => item.exact);
      const results = (exactMatches.length ? exactMatches : matches).sort((a, b) => b.score - a.score).map(item => item.node);
      const count = this.dataset.resultsCount.replace('[count]', results.length);
      this.panel.append(element('p', `${prefix}count type-caption`, count));
      if (results.length) this.panel.append(this.list(results, true));
      else {
        this.panel.append(this.querySelector('[data-mm-empty]').content.cloneNode(true));
        this.panel.append(this.goButton(this.dataset.categoriesLabel, 'root', 'button button--utility type-button'));
      }
      const shopSearch = this.querySelector('[data-mm-shop-search]').content.cloneNode(true);
      const link = shopSearch.querySelector('a');
      const url = new URL(link.href, location.href);
      url.searchParams.set('q', this.query);
      url.searchParams.set('options[prefix]', 'last');
      link.href = url.href;
      this.panel.append(shopSearch);
      this.statusTimer = setTimeout(() => { this.querySelector('[data-mm-status]').textContent = count; }, 180);
    }
    this.scroller.scrollTop = this.searchScroll;
  }

  focusHeading() {
    cancelAnimationFrame(this.focusFrame);
    this.focusFrame = requestAnimationFrame(() => { if (this.isOpen && this.isConnected) this.heading.focus({ preventScroll: true }); });
  }
}

if (!customElements.get('editorial-mobile-menu')) customElements.define('editorial-mobile-menu', EditorialMobileMenu);
